import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { TenantSettingListMaintenanceDto } from '@shared/src/controllers/maintenance/tenant/TenantSettingListMaintenanceDto';
import { TenantSettingMaintenanceController } from '@shared/src/controllers/maintenance/tenant/TenantSettingMaintenanceController';
import { MaintenanceSearchByTextDto } from '@shared/src/controllers/maintenance/_base/MaintenanceSearchByTextDtos';
import { HomeController } from '@shared/src/controllers/home/home.controller';
import { HString } from '@shared/src/datatypes/HString';
import { TenantSettingCategoryEntry, TenantSettingCategoryNode } from './TenantSettingCategoryNode';

/***
 * Pantalla de configuració a l'estil "VSCode settings": arbre de
 * classificació + cercador a l'esquerra, i a la dreta tots els settings
 * classificats sota el node seleccionat (via shared-tenantsetting-value,
 * que desa cada canvi immediatament, sense botó "Desar").
 */
@Component({
  selector: 'shared-tenantsetting-tree',
  templateUrl: './shared.tenantsetting.tree.component.html',
  styleUrls: ['./shared.tenantsetting.tree.component.scss']
})
export class SharedTenantSettingTreeComponent implements OnInit {

  public loading: boolean = true;
  public useIonic: boolean = false;

  public root: TenantSettingCategoryNode = new TenantSettingCategoryNode();
  public selectedNode: TenantSettingCategoryNode = null;

  public searchText: string = "";
  public filteredRoot: TenantSettingCategoryNode = null;

  public iReadonly: boolean = false;

  constructor(
    private tenantSettingMaintenanceController: TenantSettingMaintenanceController,
    private translate: TranslateService,
    @Inject('HomeController') protected homeController: HomeController) {
    if (homeController.useIonic)
      this.useIonic = homeController.useIonic();
  }

  ngOnInit() {
    this.load();
  }

  private load() {
    this.loading = true;
    // Els ~417 settings hi caben amb marge en una sola pàgina; no cal paginació per a l'arbre.
    let request = MaintenanceSearchByTextDto.buildPage("", [], 0, 1000);
    this.tenantSettingMaintenanceController.search(request).subscribe(result => {
      this.loading = false;
      if (result == null || result.list == null)
        return;
      this.root = this.buildTree(result.list);
      this.filteredRoot = this.root;
      if (this.root.children.length > 0)
        this.selectNode(this.root.children[0]);
    });
  }

  private buildTree(settings: TenantSettingListMaintenanceDto[]): TenantSettingCategoryNode {
    let root = new TenantSettingCategoryNode();
    root.caption = "";

    for (let setting of settings) {
      let classificationKey = 'TENANTSETTING.' + setting.setting.toString().toUpperCase() + '.CLASSIFICATION';
      let classification = this.translate.instant(classificationKey);
      if (HString.isNullOrNullString(classification) || classification === classificationKey)
        continue;

      let paths = classification.split(';').map(p => p.trim()).filter(p => p !== "");
      for (let path of paths) {
        let segments = path.split(',').map(s => s.trim()).filter(s => s !== "");
        if (segments.length === 0)
          continue;

        let node = root;
        let order: number = null;
        for (let segment of segments) {
          let parts = segment.split('#');
          let id = parts[0].trim();
          if (id === "")
            continue;
          order = parts.length > 1 && !isNaN(+parts[1]) ? +parts[1] : null;
          node = node.findOrCreateChild(id);
          node.caption = this.categoryCaption(id);
        }

        let entry = new TenantSettingCategoryEntry();
        entry.setting = setting;
        entry.order = order;
        node.entries.push(entry);
      }
    }

    root.sortChildren();
    return root;
  }

  private categoryCaption(id: string): string {
    let key = 'TENANTSETTING.CATEGORY.' + id.toUpperCase() + '.CAPTION';
    let caption = this.translate.instant(key);
    return HString.isNullOrNullString(caption) || caption === key ? id : caption;
  }

  public selectNode(node: TenantSettingCategoryNode) {
    this.selectedNode = node;
  }

  public toggleExpanded(node: TenantSettingCategoryNode, $event: MouseEvent) {
    $event.stopPropagation();
    node.expanded = !node.expanded;
  }

  public onSettingSaved(_updated: TenantSettingListMaintenanceDto) {
    // El value ja s'actualitza in-place a l'objecte compartit entre l'arbre i l'editor; res més a fer.
  }

  public onSearchTextChanged(value: string) {
    this.searchText = value;
    if (HString.isNullOrNullString(this.searchText)) {
      this.filteredRoot = this.root;
      return;
    }

    let needle = this.searchText.toLowerCase();
    this.filteredRoot = this.filterNode(this.root, needle);
    if (this.filteredRoot != null)
      this.expandAll(this.filteredRoot);
  }

  private expandAll(node: TenantSettingCategoryNode) {
    node.expanded = true;
    node.children.forEach(c => this.expandAll(c));
  }

  private settingCaption(entry: TenantSettingCategoryEntry): string {
    let key = 'TENANTSETTING.' + entry.setting.setting.toString().toUpperCase() + '.CAPTION';
    let caption = this.translate.instant(key);
    return HString.isNullOrNullString(caption) ? entry.setting.setting.toString() : caption;
  }

  private filterNode(node: TenantSettingCategoryNode, needle: string): TenantSettingCategoryNode {
    let matchedEntries = node.entries.filter(e =>
      this.settingCaption(e).toLowerCase().includes(needle) ||
      e.setting.setting.toString().toLowerCase().includes(needle));

    let matchedChildren: TenantSettingCategoryNode[] = [];
    for (let child of node.children) {
      let filteredChild = this.filterNode(child, needle);
      if (filteredChild != null)
        matchedChildren.push(filteredChild);
    }

    let selfMatches = (node.caption || "").toLowerCase().includes(needle);

    if (matchedEntries.length === 0 && matchedChildren.length === 0 && !selfMatches)
      return null;

    let result = new TenantSettingCategoryNode();
    result.id = node.id;
    result.caption = node.caption;
    result.expanded = node.expanded;
    // Si tota la categoria fa match pel nom (no per un fill/entry concret), l'ensenyem sencera
    result.entries = selfMatches && matchedEntries.length === 0 && matchedChildren.length === 0 ? node.entries : matchedEntries;
    result.children = matchedChildren;
    return result;
  }

}
